import React from 'react';
import BlueCallToAction from '../../components/common/BlueCallToAction';

/**
 * SystemAdminPage component for technical administrators
 * Provides detailed information about system architecture, security, and integration
 */
const SystemAdminPage = () => {
  // Technical implementation details
  const technicalSections = [
    {
      title: "Azure Storage Integration",
      details: [
        "Secure Azure Blob Storage for claims data ingestion",
        "Automated file validation and preprocessing",
        "Role-based access control (RBAC) implementation",
        "Encryption at rest and in transit",
        "Audit logging and monitoring"
      ]
    },
    {
      title: "Snowflake Data Warehouse",
      details: [
        "Automated data pipeline from Azure to Snowflake",
        "Optimized query performance for claims analysis",
        "Version control for data models",
        "Scalable compute resources",
        "Historical data retention policies"
      ]
    },
    {
      title: "Authentication & Security",
      details: [
        "Microsoft Authenticator integration",
        "Multi-factor authentication (MFA)",
        "Single Sign-On (SSO) implementation",
        "Session management and timeout policies",
        "Security audit trails"
      ]
    },
    {
      title: "Email Notification System",
      details: [
        "Secure email delivery infrastructure",
        "Two-step authorization process",
        "Automated reminder system",
        "Email template management",
        "Delivery status tracking"
      ]
    }
  ];

  // FAQ data structure focused on technical implementation
  const faqs = [
    {
      question: "How is data securely transferred to Azure Storage?",
      answer: "Claims data is transferred to Azure Blob Storage using secure HTTPS endpoints. All data is encrypted in transit using TLS 1.2+ and at rest using Azure Storage Service Encryption. Access is controlled through Azure Active Directory integration and SAS tokens with defined expiration periods."
    },
    {
      question: "What is the data flow between Azure and Snowflake?",
      answer: "Data is automatically ingested from Azure Blob Storage to Snowflake using Snowpipe for real-time processing. The pipeline includes data validation, transformation, and standardization steps. Snowflake's secure views and row-level security ensure data access compliance."
    },
    {
      question: "How does the Microsoft Authenticator integration work?",
      answer: "Users are required to authenticate using Microsoft Authenticator for MFA. The system integrates with Azure AD B2C for identity management, supporting conditional access policies and device compliance requirements. Authentication tokens are securely managed with appropriate expiration and refresh policies."
    },
    {
      question: "What is the email approval workflow process?",
      answer: "Summary reports are generated and sent via secure email channels. Recipients must authenticate through a secure 'I authorize this' link, which validates their identity and records their approval. The system maintains an audit trail of all approvals and includes automated follow-up for pending authorizations."
    },
    {
      question: "How are system updates and maintenance handled?",
      answer: "Updates follow a staged deployment process through development, testing, and production environments. Maintenance windows are scheduled during off-peak hours, with automated failover capabilities. All changes are version controlled and documented in our change management system."
    },
    {
      question: "What monitoring and alerting systems are in place?",
      answer: "Comprehensive monitoring using Azure Monitor and Application Insights tracks system health, performance metrics, and security events. Automated alerts are configured for critical thresholds, with escalation paths defined for different severity levels."
    }
  ];

  return (
    <div className="system-admin-page">
      {/* Hero Section */}
      <section className="py-20" style={{
        background: `linear-gradient(to top, #FFC000 0%, #FFFFFF 3%, #FFFFFF 97%, #FFC000 100%)`
      }}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 text-gray-900">
              System <span style={{ color: '#FFC000' }}>Architecture</span>
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-gray-700">
              Enterprise-grade infrastructure leveraging Azure, Snowflake, and
              secure authentication for automated claims processing.
            </p>
          </div>
        </div>
      </section>

      {/* Architecture Overview Section */}
      <section className="py-16 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center text-gray-900 dark:text-white">
            Technical Implementation
          </h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {technicalSections.map((section, index) => (
              <div 
                key={index}
                className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 shadow-sm"
              >
                <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                  {section.title}
                </h3>
                <ul className="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-300">
                  {section.details.map((detail, idx) => (
                    <li key={idx}>{detail}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Data Flow Diagram Section */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center text-gray-900 dark:text-white">
            System Flow
          </h2>
          <div className="max-w-4xl mx-auto bg-white dark:bg-gray-800 rounded-lg p-8 shadow-lg">
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="text-center p-4 bg-blue-100 dark:bg-blue-900 rounded-lg">
                  <p className="font-semibold">Claims Data Ingestion</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Azure Blob Storage</p>
                </div>
                <div className="text-4xl">→</div>
                <div className="text-center p-4 bg-blue-100 dark:bg-blue-900 rounded-lg">
                  <p className="font-semibold">Data Processing</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Snowflake Warehouse</p>
                </div>
                <div className="text-4xl">→</div>
                <div className="text-center p-4 bg-blue-100 dark:bg-blue-900 rounded-lg">
                  <p className="font-semibold">Report Generation</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Automated Analysis</p>
                </div>
              </div>
              <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                <div className="flex items-center justify-between">
                  <div className="text-center p-4 bg-green-100 dark:bg-green-900 rounded-lg">
                    <p className="font-semibold">Secure Email Delivery</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Authorization Required</p>
                  </div>
                  <div className="text-4xl">←</div>
                  <div className="text-center p-4 bg-green-100 dark:bg-green-900 rounded-lg">
                    <p className="font-semibold">Authentication</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Microsoft Authenticator</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technical FAQ Section */}
      <section className="py-16 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center text-gray-900 dark:text-white">
            Technical FAQ
          </h2>
          <div className="max-w-4xl mx-auto space-y-8">
            {faqs.map((faq, index) => (
              <div 
                key={index} 
                className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 shadow-sm"
              >
                <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                  {faq.question}
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Features Section */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center text-gray-900 dark:text-white">
            Security Features
          </h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="text-center p-6">
              <div className="text-4xl mb-4" style={{ color: '#FFC000' }}>🔒</div>
              <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                End-to-End Encryption
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Data encrypted at rest and in transit using industry-standard protocols.
              </p>
            </div>
            <div className="text-center p-6">
              <div className="text-4xl mb-4" style={{ color: '#FFC000' }}>🔐</div>
              <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                Multi-Factor Authentication
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Secure access through Microsoft Authenticator and conditional policies.
              </p>
            </div>
            <div className="text-center p-6">
              <div className="text-4xl mb-4" style={{ color: '#FFC000' }}>📋</div>
              <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                Audit Logging
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Comprehensive activity tracking and security event monitoring.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Call-to-Action Section */}
      <BlueCallToAction 
        heading="Ready to implement our secure claims processing solution?"
        description="Schedule a technical consultation to discuss your specific implementation requirements."
        primaryButtonText="Schedule Consultation"
        primaryButtonLink="/consultation"
        secondaryButtonText="View Documentation"
        secondaryButtonLink="/documentation"
      />
    </div>
  );
};

export default SystemAdminPage;


